import { clsx } from "clsx";
import {
  FC,
  FocusEvent,
  KeyboardEvent,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTextAreaResize } from "~/hooks/useTextAreaResize";
import { hasActiveTextSelection } from "~/utils/textInputUtils";
import { Icon } from "../Icons/Icons";
// TODO: Move hook to indigo-components
import { LabelBlock } from "../LabelBlock";
import type { SuggestionBlockProps } from "../SuggestionBlock";
import { SuggestionBlock } from "../SuggestionBlock";
import { Tooltip } from "../Tooltip";
import "./TextField.css";

/**
 * Base props for the TextField component
 */
export interface TextFieldProps {
  /** Accessible name for the textarea. */
  "aria-label"?: string;
  /**
   * Visual style variant of the text field
   * @default "filled"
   */
  variant?: "outline" | "filled" | "naked";

  /**
   * The current value of the text field
   */
  value: string | number | null;

  /**
   * Callback fired when the value changes
   * @param value - The new value
   */
  onChange?: (value: string) => void;

  /**
   * Placeholder text when the field is empty
   * @default "Enter text" or "-" when disabled
   */
  placeholder?: string;

  /**
   * Callback fired when a key is pressed down
   */
  onKeyDown?: (event: KeyboardEvent) => void;

  /**
   * Callback fired when the textarea loses focus
   */
  onBlur?: (event: FocusEvent<HTMLTextAreaElement>) => void;

  /**
   * Additional CSS class for the textarea element
   */
  className?: string;

  /**
   * If provided, this value will be displayed instead of the actual value
   */
  displayValue?: string;

  /**
   * Whether the field should be focused when mounted
   * @default false
   */
  isInitiallyFocused?: boolean;

  /**
   * Icon to display beside the text field
   */
  infoIcon?: ReactNode;

  /**
   * Tooltip text for the info icon
   */
  infoIconTooltip?: string | null;

  /**
   * Width of the field as a percentage
   * @default "100%"
   */
  widthPercent?: string;

  /**
   * Height of the text area content region
   * - "small": 22px text area height
   * - "default": 32px text area height
   * - "large": 40px text area height
   * - "extra-large": 112px text area height
   * @default "default"
   */
  areaSize?: "small" | "default" | "large" | "extra-large";

  /**
   * Height of the surrounding control chrome (padding, icons, etc.)
   * @default "default"
   */
  size?: "small" | "default" | "large";

  /**
   * Text field label. Accepts nodes so a label can carry inline content (e.g.
   * a link) as part of the same text run.
   */
  label?: ReactNode;

  /**
   * When true, renders a subtle spinner next to the label
   */
  isLabelLoading?: boolean;

  /**
   * Whether the field is disabled
   * @default false
   */
  disabled?: boolean;

  /**
   * Flag indicating the field should appear filled but remain read-only
   * @default false
   */
  isUnavailable?: boolean;

  /**
   * Whether the field is in an error state
   * @default false
   */
  isError?: boolean;

  /**
   * Whether the field is in a warning state (amber label / border / background /
   * description) — a non-blocking data-quality flag, distinct from `isError`.
   * @default false
   */
  isWarning?: boolean;

  /**
   * Description text
   */
  description?: ReactNode;

  /**
   * Optional inline action rendered next to the description
   */
  descriptionAction?: {
    label: string;
    onClick: () => void;
    ariaLabel?: string;
  };

  /**
   * Test ID for testing purposes
   */
  "data-testid"?: string;

  /**
   * Icon to display at the start of the text field, inside the input
   */
  startIcon?: ReactNode;

  /**
   * Whether to prevent text wrapping
   * @default false
   */
  noWrap?: boolean;

  /**
   * Icon button rendered in the top-right corner with optional tooltip
   */
  actionIcon?: ReactNode;

  /**
   * Tooltip text for the action icon
   */
  actionTooltip?: string;

  /**
   * Click handler for the action icon
   */
  onActionClick?: () => void;

  /**
   * Whether the action icon is disabled
   */
  isActionDisabled?: boolean;

  /**
   * Visually highlight the action icon to indicate active state
   */
  isActionActive?: boolean;

  /**
   * Accessible label for the action button
   */
  actionAriaLabel?: string;

  /**
   * Remove default padding from LabelBlock when true
   */
  noPadding?: boolean;

  /**
   * Tooltip text shown on hover of a "?" icon next to the label (edit mode only,
   * unless `alwaysShowInfoTooltip` is true).
   */
  infoTooltip?: string;

  /**
   * When true, the info tooltip icon is shown even when the field is disabled (display mode).
   * @default false
   */
  alwaysShowInfoTooltip?: boolean;

  /**
   * When provided and the field is disabled, renders an inline copy icon button
   * immediately after the value text.
   */
  copyValue?: string | null;

  /**
   * Optional suggestion block rendered below the text field input.
   * Pass SuggestionBlockProps to render a SuggestionBlock component.
   */
  suggestion?: SuggestionBlockProps;
}

/**
 * TextField Component
 *
 * A flexible text field component that supports multiple lines of text
 * and automatic height adjustment.
 *
 * Features:
 * - Auto-resizing text area
 * - Support for additional info icon with tooltip
 * - Accessible labeling options
 * - Error state styling
 * - Multiple variants: outline, filled, naked
 *
 * @example
 * // Basic usage
 * <TextField
 *   label="Notes"
 *   value={notes}
 *   onChange={setNotes}
 * />
 *
 * @example
 * // With info icon and tooltip
 * <TextField
 *   label="Tags"
 *   value={tags}
 *   onChange={setTags}
 *   infoIcon={<InfoIcon />}
 *   infoIconTooltip="Enter comma-separated tags"
 * />
 *
 * @example
 * // With start icon
 * <TextField
 *   label="Search"
 *   value={search}
 *   onChange={setSearch}
 *   startIcon={<SearchIcon />}
 * />
 *
 * @example
 * // Naked variant - appears as text until focused
 * <TextField
 *   variant="naked"
 *   value={editableText}
 *   onChange={setEditableText}
 * />
 */
export const TextField: FC<TextFieldProps> = ({
  variant = "filled",
  value,
  onChange,
  disabled = false,
  isUnavailable = false,
  placeholder,
  onKeyDown,
  onBlur,
  className,
  displayValue,
  isInitiallyFocused,
  infoIcon,
  infoIconTooltip,
  widthPercent,
  areaSize = "default",
  size = "default",
  label,
  isLabelLoading,
  isError,
  isWarning,
  description,
  descriptionAction,
  "data-testid": dataTestId,
  "aria-label": ariaLabel,
  startIcon,
  noWrap = false,
  actionIcon,
  actionTooltip,
  onActionClick,
  isActionDisabled,
  isActionActive,
  actionAriaLabel,
  noPadding,
  infoTooltip,
  alwaysShowInfoTooltip,
  copyValue,
  suggestion,
  ...additionalProps
}) => {
  // Use default placeholder if none provided
  const finalPlaceholder = placeholder ?? (disabled ? "-" : "Enter text");

  // Reference to the textarea element for auto-resizing
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [showCopiedTooltip, setShowCopiedTooltip] = useState(false);
  const resolvedValue = displayValue ?? (value !== null ? String(value) : "");

  // Use the auto-resize hook to adjust height based on content
  const resize = useTextAreaResize(textareaRef);

  // Resize the textarea when value changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing; deps reviewed, auto-fix risks behavior change
  useEffect(() => {
    resize();
  }, [value, displayValue, resize]);

  const sanitizedClassName = className
    ? className
        .split(" ")
        .filter(
          (token) =>
            token.trim() &&
            token.trim() !== "indigo-textfield-textarea--with-action",
        )
        .join(" ")
        .trim() || undefined
    : undefined;

  // Handler for copy on click in disabled state
  const handleCopyOnClick = () => {
    if (hasActiveTextSelection()) {
      return;
    }

    if (disabled || isUnavailable) {
      const textToCopy = resolvedValue;
      if (textToCopy && textToCopy !== "-") {
        navigator.clipboard
          .writeText(textToCopy)
          .then(() => {
            setShowCopiedTooltip(true);
            setTimeout(() => setShowCopiedTooltip(false), 2000);
          })
          .catch(() => {
            // Silently fail if clipboard access is denied
          });
      }
    }
  };

  // Main TextField component
  const hasAction = Boolean(actionIcon);
  const actionDisabled = isActionDisabled ?? !onActionClick;

  const labelAdornment = isLabelLoading ? (
    <Icon name="spinner" size="xs" className="indigo-textfield-label-spinner" />
  ) : undefined;

  const actionButton = hasAction ? (
    actionTooltip ? (
      <Tooltip content={actionTooltip} placement="top-start">
        <button
          type="button"
          className={clsx(
            "indigo-textfield-action-button",
            isActionActive && "indigo-textfield-action-button--active",
          )}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (!actionDisabled) {
              onActionClick?.();
            }
          }}
          aria-label={actionAriaLabel || actionTooltip}
          aria-pressed={
            typeof isActionActive === "boolean" ? isActionActive : undefined
          }
          disabled={actionDisabled}
        >
          {actionIcon}
        </button>
      </Tooltip>
    ) : (
      <button
        type="button"
        className={clsx(
          "indigo-textfield-action-button",
          isActionActive && "indigo-textfield-action-button--active",
        )}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (!actionDisabled) {
            onActionClick?.();
          }
        }}
        aria-label={actionAriaLabel}
        aria-pressed={
          typeof isActionActive === "boolean" ? isActionActive : undefined
        }
        disabled={actionDisabled}
      >
        {actionIcon}
      </button>
    )
  ) : null;

  const resolvedDescription = descriptionAction ? (
    <div className="indigo-textfield-description">
      {description && (
        <span className="indigo-textfield-description-text">{description}</span>
      )}
      <div className="indigo-textfield-description-action-wrapper">
        {description && (
          <span
            className="indigo-textfield-description-separator"
            aria-hidden="true"
          >
            &middot;
          </span>
        )}
        <button
          type="button"
          className="indigo-textfield-description-action"
          onClick={() => descriptionAction.onClick()}
          aria-label={descriptionAction.ariaLabel || descriptionAction.label}
        >
          {descriptionAction.label}
        </button>
      </div>
    </div>
  ) : (
    description
  );

  const textFieldClassNames = clsx(
    "indigo-textfield-textarea",
    variant === "filled" && "indigo-textfield-textarea--filled",
    variant === "naked" && "indigo-textfield-textarea--naked",
    disabled && "indigo-textfield-textarea-disabled",
    isError && "indigo-textfield-textarea-error",
    // Warning box (border/bg) only in edit mode — a boxed read-only value looks
    // editable. The amber label + description still convey it in display mode.
    isWarning && !isError && !disabled && "indigo-textfield-textarea-warning",
    // In display mode the 32px control min-height leaves dead space under the
    // value; when a description sits below it, drop the min-height so the two
    // aren't pushed apart.
    disabled &&
      Boolean(description) &&
      "indigo-textfield-textarea--display-tight",
    areaSize === "small" && "indigo-textfield-textarea-sm",
    areaSize === "large" && "indigo-textfield-textarea-large",
    areaSize === "extra-large" && "indigo-textfield-textarea-xl",
    startIcon && "indigo-textfield-textarea--with-start-icon",
    noWrap && "indigo-textfield-textarea--nowrap",
    isUnavailable && "indigo-textfield-textarea-unavailable",
    sanitizedClassName,
  );

  const isReadOnly = disabled || isUnavailable;

  // When copyValue is set and field is read-only, render inline text + copy icon
  // (span sizes to content so the icon appears right after the text)
  if (copyValue && isReadOnly) {
    return (
      <LabelBlock
        {...additionalProps}
        label={label}
        disabled={disabled}
        isError={isError}
        isWarning={isWarning}
        description={resolvedDescription}
        widthPercent={widthPercent}
        labelAdornment={labelAdornment}
        noPadding={noPadding}
        infoTooltip={infoTooltip}
        alwaysShowInfoTooltip={alwaysShowInfoTooltip}
      >
        <div className="indigo-textfield-copy-inline">
          <span className="indigo-textfield-copy-text" data-testid={dataTestId}>
            {resolvedValue || finalPlaceholder}
          </span>
          <Tooltip content="Copy" placement="top-start">
            <button
              type="button"
              className="indigo-textfield-copy-button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                void navigator.clipboard.writeText(copyValue);
              }}
              aria-label="Copy value"
            >
              <Icon name="copy" size="s" />
            </button>
          </Tooltip>
        </div>
      </LabelBlock>
    );
  }

  return (
    <LabelBlock
      {...additionalProps}
      label={label}
      disabled={disabled}
      isError={isError}
      isWarning={isWarning}
      description={resolvedDescription}
      widthPercent={widthPercent}
      actionMenu={actionButton}
      labelAdornment={labelAdornment}
      noPadding={noPadding}
      infoTooltip={infoTooltip}
      alwaysShowInfoTooltip={alwaysShowInfoTooltip}
    >
      <div className="indigo-textfield-wrapper">
        <Tooltip
          content="Copied!"
          placement="top-start"
          size="sm"
          visible={showCopiedTooltip}
        >
          <div
            className={clsx(
              "indigo-textfield-input-container",
              `indigo-textfield-input-container--${size}`,
            )}
            onClick={handleCopyOnClick}
            style={disabled || isUnavailable ? { cursor: "text" } : undefined}
          >
            {startIcon && (
              <div className="indigo-textfield-start-icon" aria-hidden="true">
                {startIcon}
              </div>
            )}
            {infoIcon && infoIconTooltip ? (
              <Tooltip content={infoIconTooltip} placement="top-start">
                <div className="indigo-textfield-info-icon">{infoIcon}</div>
              </Tooltip>
            ) : (
              infoIcon && (
                <div className="indigo-textfield-info-icon">{infoIcon}</div>
              )
            )}
            <textarea
              ref={textareaRef}
              aria-label={ariaLabel}
              value={resolvedValue}
              onChange={(e) => {
                onChange?.(e.target.value);
              }}
              readOnly={isReadOnly}
              placeholder={finalPlaceholder}
              className={textFieldClassNames}
              onKeyDown={onKeyDown}
              onBlur={onBlur}
              rows={1}
              autoFocus={isInitiallyFocused}
              data-testid={dataTestId}
              aria-readonly={isReadOnly || undefined}
              aria-disabled={disabled || undefined}
              tabIndex={disabled ? -1 : undefined}
            />
          </div>
        </Tooltip>
        {suggestion && <SuggestionBlock {...suggestion} />}
      </div>
    </LabelBlock>
  );
};

export default TextField;
